/**
 * IndexedDB wrapper with localStorage-like API
 * Provides a simple interface for storing and retrieving data from IndexedDB
 */

const DB_NAME = 'AppCache';
const DB_VERSION = 1;
const STORE_NAME = 'cache';

// Initialize IndexedDB
const initIndexedDB = () => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME, { keyPath: 'key' });
            }
        };

        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
    });
};

// Create the DB namespace object
const DB = {
    // Set item in IndexedDB (similar to localStorage.setItem)
    setItem: async (key, value) => {
        try {
            const db = await initIndexedDB();
            const transaction = db.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);

            return new Promise((resolve, reject) => {
                const putRequest = store.put({
                    key,
                    value,
                    timestamp: Date.now()
                });
                putRequest.onsuccess = () => resolve();
                putRequest.onerror = () => reject(putRequest.error);
            });
        } catch (error) {
            console.error('IndexedDB setItem error:', error);
            throw error;
        }
    },

    // Get item from IndexedDB (similar to localStorage.getItem)
    getItem: async (key) => {
        try {
            const db = await initIndexedDB();
            const transaction = db.transaction([STORE_NAME], 'readonly');
            const store = transaction.objectStore(STORE_NAME);

            return new Promise((resolve, reject) => {
                const getRequest = store.get(key);
                getRequest.onsuccess = () => {
                    const result = getRequest.result;
                    if (result && result.value !== undefined) {
                        resolve(result.value);
                    } else {
                        resolve(null);
                    }
                };
                getRequest.onerror = () => reject(getRequest.error);
            });
        } catch (error) {
            console.error('IndexedDB getItem error:', error);
            return null;
        }
    },

    // Remove item from IndexedDB (similar to localStorage.removeItem)
    removeItem: async (key) => {
        try {
            const db = await initIndexedDB();
            const transaction = db.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);

            return new Promise((resolve, reject) => {
                const deleteRequest = store.delete(key);
                deleteRequest.onsuccess = () => resolve();
                deleteRequest.onerror = () => reject(deleteRequest.error);
            });
        } catch (error) {
            console.error('IndexedDB removeItem error:', error);
            throw error;
        }
    },

    // Clear all items from IndexedDB (similar to localStorage.clear)
    clear: async () => {
        try {
            const db = await initIndexedDB();
            const transaction = db.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);

            return new Promise((resolve, reject) => {
                const clearRequest = store.clear();
                clearRequest.onsuccess = () => resolve();
                clearRequest.onerror = () => reject(clearRequest.error);
            });
        } catch (error) {
            console.error('IndexedDB clear error:', error);
            throw error;
        }
    },

    // Get all keys from IndexedDB (similar to Object.keys(localStorage))
    keys: async () => {
        try {
            const db = await initIndexedDB();
            const transaction = db.transaction([STORE_NAME], 'readonly');
            const store = transaction.objectStore(STORE_NAME);

            return new Promise((resolve, reject) => {
                const getAllRequest = store.getAllKeys();
                getAllRequest.onsuccess = () => resolve(getAllRequest.result);
                getAllRequest.onerror = () => reject(getAllRequest.error);
            });
        } catch (error) {
            console.error('IndexedDB keys error:', error);
            return [];
        }
    },

    // Clean up expired entries
    cleanupExpired: async () => {
        try {
            const db = await initIndexedDB();
            const transaction = db.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);

            return new Promise((resolve, reject) => {
                const getAllRequest = store.getAll();
                getAllRequest.onsuccess = () => {
                    const now = Date.now();
                    const expiredKeys = getAllRequest.result
                        .filter(item => item.value && item.value.expiry && item.value.expiry < now)
                        .map(item => item.key);

                    // Delete expired entries
                    expiredKeys.forEach(key => {
                        store.delete(key);
                    });
                    resolve(expiredKeys.length);
                };
                getAllRequest.onerror = () => reject(getAllRequest.error);
            });
        } catch (error) {
            console.warn('IndexedDB cleanup error:', error);
            return 0;
        }
    },

    // Get storage usage info
    getStorageInfo: async () => {
        try {
            const db = await initIndexedDB();
            const transaction = db.transaction([STORE_NAME], 'readonly');
            const store = transaction.objectStore(STORE_NAME);

            return new Promise((resolve, reject) => {
                const countRequest = store.count();
                countRequest.onsuccess = () => {
                    resolve({
                        itemCount: countRequest.result,
                        // Note: IndexedDB doesn't provide exact size info like localStorage
                        // This is an approximation
                        estimatedSize: 'Unknown (IndexedDB doesn\'t provide size info)'
                    });
                };
                countRequest.onerror = () => reject(countRequest.error);
            });
        } catch (error) {
            console.error('IndexedDB getStorageInfo error:', error);
            return { itemCount: 0, estimatedSize: 'Error' };
        }
    }
};

// Change the export to named export
export { DB };